import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { Heart } from "lucide-react";

export default function SharedWishlist() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/wishlist/shared/${token}`)
      .then(({ data }) => setData(data))
      .catch(() => setError("This wishlist link is no longer available."))
      .finally(() => setLoading(false));
  }, [token]);

  const items = data?.products || [];
  const owner = data?.owner;

  return (
    <div className="max-w-[1800px] mx-auto px-5 sm:px-8 lg:px-16 py-14 min-h-[70vh]" data-testid="shared-wishlist-page">
      <Seo title={owner ? `${owner}'s Wishlist` : "Shared Wishlist"} path={`/wishlist/shared/${token}`} noindex />
      <Reveal className="text-center mb-10">
        <p className="text-xs uppercase tracking-[0.28em] text-pink">Shared With You</p>
        <h1 className="font-serif text-5xl lg:text-6xl mt-3 text-charcoal">
          {owner ? `${owner}'s Wishlist` : "A Shared Wishlist"}
        </h1>
        <div className="w-16 h-px bg-gold mx-auto mt-6" />
        <p className="text-gray-500 mt-5 text-sm">These are the pieces they've saved — feel free to shop any of them.</p>
      </Reveal>

      {loading ? (
        <p className="text-center text-gray-400 py-20">Loading wishlist...</p>
      ) : error ? (
        <div className="text-center py-20" data-testid="shared-wishlist-error">
          <Heart className="w-14 h-14 text-gray-200 mx-auto" />
          <p className="text-gray-500 mt-5">{error}</p>
          <Link to="/shop/all" className="inline-block mt-8 bg-pink text-white px-9 py-4 text-xs uppercase tracking-[0.18em] hover:bg-pink-hover transition-colors">
            Browse Collections
          </Link>
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-20" data-testid="shared-wishlist-empty">
          <Heart className="w-14 h-14 text-gray-200 mx-auto" />
          <p className="text-gray-500 mt-5">This wishlist is empty right now.</p>
          <Link to="/shop/all" className="inline-block mt-8 bg-pink text-white px-9 py-4 text-xs uppercase tracking-[0.18em] hover:bg-pink-hover transition-colors">
            Browse Collections
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
          {items.map((p, i) => (
            <Reveal key={p.id} delay={(i % 4) * 0.06}>
              <ProductCard product={p} />
            </Reveal>
          ))}
        </div>
      )}
    </div>
  );
}
